import { BLEND_OPTIONS, type BannerEditing, type BannerLayer } from '../model/bannerLayers';
import {
  Block,
  ColorField,
  CheckRow,
  Drop,
  Hint,
  InspectorSection,
  NumberField,
  Row,
  Seg,
  SliderField,
  TextField,
} from './controls';

/** Property panel for the selected banner layer — geometry, appearance, and
 *  kind-specific controls, all writing through the undo history. */
export function LayerInspector({ editing }: { editing: BannerEditing }) {
  const { layers, selectedId, onPatchLayer: patchLayer } = editing;
  const layer = layers.find((l) => l.id === selectedId);

  if (!layer) {
    return (
      <div className="p-4">
        <Hint>
          Select a layer on the canvas or in the Layers panel to edit its properties. Add layers
          from the panel on the left.
        </Hint>
      </div>
    );
  }

  const geom = (patch: Partial<BannerLayer>) => patchLayer(layer.id, patch, `insp-geom-${layer.id}`);

  return (
    <>
      <InspectorSection title="Layer">
        <Row label="Name">
          <TextField value={layer.name} maxLength={40} onChange={(name) => patchLayer(layer.id, { name })} />
        </Row>
        <Row label="Opacity">
          <SliderField value={layer.opacity} onChange={(opacity) => patchLayer(layer.id, { opacity }, `op-${layer.id}`)} />
        </Row>
        <Row label="Blend">
          <Drop value={layer.blend} options={BLEND_OPTIONS} onChange={(blend) => patchLayer(layer.id, { blend })} />
        </Row>
      </InspectorSection>

      <InspectorSection title="Position & size">
        <div className="grid grid-cols-2 gap-2">
          <Row label="X"><NumberField value={layer.x} onChange={(x) => geom({ x })} suffix="px" /></Row>
          <Row label="Y"><NumberField value={layer.y} onChange={(y) => geom({ y })} suffix="px" /></Row>
          <Row label="W"><NumberField value={layer.w} min={16} onChange={(w) => geom({ w: Math.max(16, w) })} suffix="px" /></Row>
          <Row label="H"><NumberField value={layer.h} min={16} onChange={(h) => geom({ h: Math.max(16, h) })} suffix="px" /></Row>
        </div>
      </InspectorSection>

      {layer.kind === 'text' && (
        <InspectorSection title="Text">
          <Block>
            <TextField value={layer.text} maxLength={120} onChange={(text) => patchLayer(layer.id, { text }, `txt-${layer.id}`)} />
          </Block>
          <Row label="Size">
            <SliderField
              value={layer.size}
              min={16}
              max={320}
              step={2}
              format={(v) => `${Math.round(v)}`}
              onChange={(size) => patchLayer(layer.id, { size }, `size-${layer.id}`)}
            />
          </Row>
          <Row label="Weight">
            <Seg
              value={String(layer.weight) as '400'}
              options={[
                { id: '400', label: 'Reg' },
                { id: '700', label: 'Bold' },
                { id: '900', label: 'Black' },
              ]}
              onChange={(w) => patchLayer(layer.id, { weight: Number(w) as 400 })}
            />
          </Row>
          <Row label="Align">
            <Seg
              value={layer.align}
              options={[
                { id: 'left', label: 'Left' },
                { id: 'center', label: 'Center' },
                { id: 'right', label: 'Right' },
              ]}
              onChange={(align) => patchLayer(layer.id, { align })}
            />
          </Row>
          <Row label="Color">
            <ColorField value={layer.color} onChange={(color) => patchLayer(layer.id, { color }, `col-${layer.id}`)} />
          </Row>
          <CheckRow label="Drop shadow" checked={layer.shadow} onChange={(shadow) => patchLayer(layer.id, { shadow })} />
        </InspectorSection>
      )}

      {layer.kind === 'shape' && (
        <InspectorSection title="Shape">
          <Row label="Shape">
            <Seg
              value={layer.shape}
              options={[
                { id: 'rect', label: 'Rectangle' },
                { id: 'ellipse', label: 'Ellipse' },
              ]}
              onChange={(shape) => patchLayer(layer.id, { shape })}
            />
          </Row>
          <Row label="Fill">
            <ColorField value={layer.fill} onChange={(fill) => patchLayer(layer.id, { fill }, `fill-${layer.id}`)} />
          </Row>
          {layer.shape === 'rect' && (
            <Row label="Radius">
              <SliderField value={layer.radius} min={0} max={300} step={2} format={(v) => `${Math.round(v)}`} onChange={(radius) => patchLayer(layer.id, { radius }, `rad-${layer.id}`)} />
            </Row>
          )}
        </InspectorSection>
      )}

      {layer.kind === 'gradient' && (
        <InspectorSection title="Gradient">
          <Row label="From">
            <ColorField value={layer.from} onChange={(from) => patchLayer(layer.id, { from }, `gf-${layer.id}`)} />
          </Row>
          <Row label="To">
            <ColorField value={layer.to} onChange={(to) => patchLayer(layer.id, { to }, `gt-${layer.id}`)} />
          </Row>
          <Row label="Angle">
            <SliderField value={layer.angle} min={0} max={360} step={5} format={(v) => `${Math.round(v)}°`} onChange={(angle) => patchLayer(layer.id, { angle }, `ga-${layer.id}`)} />
          </Row>
        </InspectorSection>
      )}

      {layer.kind === 'glass' && (
        <InspectorSection title="Glass">
          <Row label="Blur">
            <SliderField value={layer.blur} min={0} max={60} step={1} format={(v) => `${Math.round(v)}px`} onChange={(blur) => patchLayer(layer.id, { blur }, `gb-${layer.id}`)} />
          </Row>
          <Row label="Tint">
            <ColorField value={layer.tint} onChange={(tint) => patchLayer(layer.id, { tint }, `gt2-${layer.id}`)} />
          </Row>
          <Row label="Tint amt">
            <SliderField value={layer.tintOpacity} onChange={(tintOpacity) => patchLayer(layer.id, { tintOpacity }, `gto-${layer.id}`)} />
          </Row>
          <Row label="Radius">
            <SliderField value={layer.radius} min={0} max={300} step={2} format={(v) => `${Math.round(v)}`} onChange={(radius) => patchLayer(layer.id, { radius }, `gr-${layer.id}`)} />
          </Row>
        </InspectorSection>
      )}

      {layer.kind === 'image' && (
        <InspectorSection title="Image">
          <Row label="Fit">
            <Seg
              value={layer.fit}
              options={[
                { id: 'cover', label: 'Cover' },
                { id: 'contain', label: 'Contain' },
              ]}
              onChange={(fit) => patchLayer(layer.id, { fit })}
            />
          </Row>
          <Row label="Radius">
            <SliderField value={layer.radius} min={0} max={300} step={2} format={(v) => `${Math.round(v)}`} onChange={(radius) => patchLayer(layer.id, { radius }, `ir-${layer.id}`)} />
          </Row>
          {!layer.url && <Hint>Pick an image from your Assets library to fill this layer.</Hint>}
        </InspectorSection>
      )}
    </>
  );
}
